import React, { useEffect, useCallback, useRef, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Download } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Full-screen lightbox for gallery items ({ url, thumb_url, caption, kind }).
 * Keyboard: Esc / ← / →. Touch: swipe left/right. Click backdrop to close.
 */
export const Lightbox = ({ items = [], index = 0, onClose, onIndexChange }) => {
  const [loading, setLoading] = useState(true);
  const touch = useRef({ x: 0, y: 0 });
  const item = items[index];
  const count = items.length;

  const go = useCallback((delta) => {
    if (!count) return;
    setLoading(true);
    onIndexChange((index + delta + count) % count);
  }, [index, count, onIndexChange]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [go, onClose]);

  if (!item) return null;
  const src = `${BACKEND}${item.url}`;

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-md flex flex-col"
      role="dialog" aria-modal="true" aria-label={item.caption || 'Photo viewer'}
      data-testid="gallery-lightbox"
      onClick={onClose}
      onTouchStart={(e) => { touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; }}
      onTouchEnd={(e) => {
        const dx = e.changedTouches[0].clientX - touch.current.x;
        const dy = e.changedTouches[0].clientY - touch.current.y;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? 1 : -1);
      }}
    >
      {/* top bar */}
      <div className="flex items-center justify-between px-4 py-3 text-white/90" onClick={(e) => e.stopPropagation()}>
        <span className="text-sm tabular-nums" data-testid="gallery-lightbox-counter">{index + 1} / {count}</span>
        <div className="flex items-center gap-2">
          <a href={src} download target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full hover:bg-white/10 flex items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-[hsl(var(--lux-gold))]" aria-label="Open full size" data-testid="gallery-lightbox-download">
            <Download className="w-5 h-5" />
          </a>
          <button type="button" onClick={onClose} className="w-11 h-11 rounded-full hover:bg-white/10 flex items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-[hsl(var(--lux-gold))]" aria-label="Close" data-testid="gallery-lightbox-close">
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* stage */}
      <div className="flex-1 relative flex items-center justify-center px-2 sm:px-16 min-h-0">
        {count > 1 && (
          <button type="button" onClick={(e) => { e.stopPropagation(); go(-1); }} className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-[hsl(var(--lux-gold))]" aria-label="Previous" data-testid="gallery-lightbox-prev">
            <ChevronLeft className="w-7 h-7" />
          </button>
        )}
        <div className="max-w-full max-h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
          {loading && <div className="absolute inset-0 flex items-center justify-center"><div className="w-10 h-10 rounded-full border-2 border-[hsl(var(--lux-gold))] border-t-transparent animate-spin" /></div>}
          {item.kind === 'video' ? (
            <video key={item.file_id} src={src} controls autoPlay playsInline className="max-h-[75vh] max-w-full rounded-lg shadow-2xl" onLoadedData={() => setLoading(false)} data-testid="gallery-lightbox-video" />
          ) : (
            <img key={item.file_id} src={src} alt={item.caption || 'Gallery photo'} className={`max-h-[75vh] max-w-full object-contain rounded-lg shadow-2xl select-none transition-opacity duration-200 ${loading ? 'opacity-0' : 'opacity-100'}`} onLoad={() => setLoading(false)} draggable={false} data-testid="gallery-lightbox-image" />
          )}
        </div>
        {count > 1 && (
          <button type="button" onClick={(e) => { e.stopPropagation(); go(1); }} className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-[hsl(var(--lux-gold))]" aria-label="Next" data-testid="gallery-lightbox-next">
            <ChevronRight className="w-7 h-7" />
          </button>
        )}
      </div>

      {/* caption */}
      <div className="px-4 pb-6 pt-3 text-center min-h-[3.5rem]" onClick={(e) => e.stopPropagation()}>
        {item.caption && <p className="text-white/90 text-base sm:text-lg" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="gallery-lightbox-caption">{item.caption}</p>}
      </div>
    </div>
  );
};
